import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ConflictField } from '@mads/shared'
import { ConfirmDialog } from './Feedback'
import { Logo } from './Logo'
import { conflictsOf, useSyncStore, type Unresolved } from '@/lib/sync'

/**
 * The block a device goes behind when it reconnects holding a queue
 * (issue #64, step 2).
 *
 * Full screen, in the same place in the tree as the session-restore splash, and
 * nothing else in the app is reachable while it is up. That is deliberate: a
 * half-synced inventory is a worse thing to browse than none, and somebody
 * scrolling a list that is about to change under them cannot make a good
 * decision about anything on it.
 *
 * Conflicts are walked **one change at a time**. There is a per-record "keep all
 * mine", which is a reasonable convenience when somebody has just recounted a
 * shelf — but there is deliberately **no global one**, because that is the
 * button people press to make the screen go away, and the screen going away is
 * exactly what must not happen without the counts being looked at.
 */
export function SyncBlocker() {
  const queryClient = useQueryClient()
  const phase = useSyncStore((s) => s.phase)
  const unresolved = useSyncStore((s) => s.unresolved)
  const error = useSyncStore((s) => s.error)
  const applied = useSyncStore((s) => s.applied)
  const run = useSyncStore((s) => s.run)
  const dismissError = useSyncStore((s) => s.dismissError)

  const current = unresolved[0] ?? null

  return (
    <div className="sync-blocker">
      <div className="sync-blocker-inner">
        <Logo layout="stacked" theme="white" size={40} />

        {phase === 'syncing' && (
          <p className="sync-blocker-lede" role="status">
            Sending the changes made on this device…
          </p>
        )}

        {phase === 'failed' && error !== null && (
          <>
            <h1 className="sync-blocker-title">Your changes are still here</h1>
            <p className="sync-blocker-lede">{error}</p>
            <div className="sync-blocker-actions">
              <button type="button" className="btn btn-primary" onClick={() => void run(queryClient)}>
                Try again
              </button>
              <button type="button" className="btn btn-ghost" onClick={dismissError}>
                Not now
              </button>
            </div>
          </>
        )}

        {phase === 'resolving' && current !== null && (
          <Resolution
            key={current.changeset.id}
            entry={current}
            remaining={unresolved.length}
            applied={applied}
            onDone={() => void run(queryClient)}
          />
        )}
      </div>
    </div>
  )
}

function Resolution({
  entry,
  remaining,
  applied,
  onDone,
}: {
  entry: Unresolved
  remaining: number
  applied: number
  onDone: () => void
}) {
  const keepMine = useSyncStore((s) => s.keepMine)
  const keepAllMine = useSyncStore((s) => s.keepAllMine)
  const keepTheirs = useSyncStore((s) => s.keepTheirs)
  const keepForLater = useSyncStore((s) => s.keepForLater)
  const discard = useSyncStore((s) => s.discard)
  const [confirming, setConfirming] = useState(false)

  const conflicts = conflictsOf(entry)
  const rejected = entry.result.status === 'rejected'
  // A refusal that lifts by itself, so it is kept rather than only read
  // (issue #64, step 3). Branching on the code and never on the sentence.
  const waitable = entry.result.code !== undefined

  return (
    <>
      <h1 className="sync-blocker-title">
        {waitable
          ? 'One change is waiting its turn'
          : rejected
            ? 'One change could not be saved'
            : 'One change needs an answer'}
      </h1>
      <p className="sync-blocker-lede">
        {applied > 0 && `${applied} ${applied === 1 ? 'change' : 'changes'} saved. `}
        {remaining === 1
          ? 'This is the last one.'
          : `${remaining} still to deal with — one at a time.`}
      </p>

      <div className="sync-blocker-card">
        <p className="sync-blocker-what">{describe(entry)}</p>

        {rejected && (
          <>
            <p className="sync-blocker-reason">{entry.result.reason}</p>
            <p className="sync-blocker-note">
              {waitable
                ? 'Nothing is lost. Your walk stays on this device and will be saved as soon as the other one is finished — carry on counting in the meantime.'
                : 'There is nothing to keep here — the server will not take this change. Discarding it is the only way past, so read it before you do.'}
            </p>
          </>
        )}

        {conflicts.map((conflict) => (
          <ConflictRow
            key={conflict.field}
            conflict={conflict}
            onKeepMine={() => void keepMine(entry.changeset.id, conflict.field)}
            onKeepTheirs={() => void keepTheirs(entry.changeset.id, conflict.field)}
          />
        ))}
      </div>

      <div className="sync-blocker-actions">
        {conflicts.length > 1 && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void keepAllMine(entry.changeset.id)}
          >
            Keep all of mine on this record
          </button>
        )}

        {waitable && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void keepForLater(entry.changeset.id).then(onDone)}
          >
            Keep it and carry on
          </button>
        )}

        {!rejected && (
          <button type="button" className="btn btn-primary" onClick={onDone}>
            Send this change
          </button>
        )}

        {/* Discarding is destructive — somebody's count goes (spec W-03). */}
        <button type="button" className="btn btn-ghost" onClick={() => setConfirming(true)}>
          {isWalkStart(entry) ? 'Discard this walk' : 'Discard this change'}
        </button>
      </div>

      {confirming && (
        <ConfirmDialog
          title={isWalkStart(entry) ? 'Discard this walk?' : 'Discard this change?'}
          body={
            isWalkStart(entry)
              ? `${describe(entry)} Every count you took on it goes with it. This cannot be undone.`
              : `${describe(entry)} This cannot be undone, and the change will not reach the inventory.`
          }
          confirmLabel={isWalkStart(entry) ? 'Discard the walk' : 'Discard it'}
          onConfirm={() => {
            setConfirming(false)
            void discard(entry.changeset.id).then(onDone)
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  )
}

/**
 * One field, three values. What the device says and what the server says are
 * the obvious pair; what the device *thought it was changing from* is the third,
 * and it is what makes the other two make sense — "mine says 6 and theirs says
 * 8" is a different question depending on where the walker started.
 */
function ConflictRow({
  conflict,
  onKeepMine,
  onKeepTheirs,
}: {
  conflict: ConflictField
  onKeepMine: () => void
  onKeepTheirs: () => void
}) {
  return (
    <div className="sync-conflict">
      <h2 className="sync-conflict-field">{conflict.label}</h2>

      <p className="sync-conflict-base">
        You changed this from <strong>{show(conflict.base)}</strong>.
        {conflict.changedBy !== null && (
          <>
            {' '}
            {conflict.changedBy} has changed it since
            {conflict.changedAt !== null && ` (${formatWhen(conflict.changedAt)})`}.
          </>
        )}
      </p>

      <div className="sync-conflict-sides">
        <button type="button" className="sync-side" onClick={onKeepMine}>
          <span className="sync-side-label">This device</span>
          <span className="sync-side-value">{show(conflict.mine)}</span>
          <span className="sync-side-action">Keep mine</span>
        </button>

        <button type="button" className="sync-side" onClick={onKeepTheirs}>
          <span className="sync-side-label">The inventory</span>
          <span className="sync-side-value">{show(conflict.theirs)}</span>
          <span className="sync-side-action">Keep theirs</span>
        </button>
      </div>
    </div>
  )
}

function show(value: string | null): string {
  return value === null || value === '' ? 'nothing' : value
}

function formatWhen(iso: string): string {
  const date = new Date(iso)

  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
}

/**
 * Whether this is the start of a walk, which discards differently: its counts
 * and its finish go with it, and the wording has to say so.
 */
function isWalkStart(entry: Unresolved): boolean {
  const { changeset } = entry

  return changeset.entity === 'stocktake' && changeset.kind === 'create'
}

/** What the change was, in the walker's terms rather than the queue's. */
function describe(entry: Unresolved): string {
  const { changeset } = entry
  const what = {
    item: 'a piece of equipment',
    location: 'a place',
    fixtureModel: 'a fixture model',
    stocktake: 'a stocktake',
  }[changeset.entity]

  const when = formatWhen(changeset.at)

  switch (changeset.kind) {
    case 'create':
      return changeset.entity === 'stocktake'
        ? `You started a stocktake on this device (${when}).`
        : `You added ${what} on this device (${when}).`
    case 'delete':
      return `You binned ${what} on this device (${when}).`
    case 'restore':
      return `You restored ${what} on this device (${when}).`
    case 'reorder':
      return `You changed the walking order on this device (${when}).`
    case 'count':
      return `You counted a line of a stocktake on this device (${when}).`
    case 'complete':
      return `You finished a stocktake on this device (${when}).`
    case 'abandon':
      return `You abandoned a stocktake on this device (${when}).`
    case 'photograph':
      return changeset.action === 'clear'
        ? `You removed the photograph from ${what} on this device (${when}).`
        : `You took a photograph of ${what} on this device (${when}).`
    default:
      return `You changed ${what} on this device (${when}).`
  }
}
